import { listSowCreditPoolDefinitions } from '@wevo/wevo-access/sow-credit-pools';

import type { LedgerEntrySummary } from '../../../services/api';

export type SowLedgerTableViewMode = 'grouped' | 'raw';

const POOL_TYPE_DISPLAY_ORDER = listSowCreditPoolDefinitions().map((definition) => definition.type);

/** Stable catalog order for pool filter chips and labels. */
export function sortPoolTypesForDisplay(poolTypes: Iterable<string>): string[] {
    const unique = [...new Set(poolTypes)];
    return unique.sort((a, b) => {
        const aIndex = POOL_TYPE_DISPLAY_ORDER.indexOf(
            a as (typeof POOL_TYPE_DISPLAY_ORDER)[number],
        );
        const bIndex = POOL_TYPE_DISPLAY_ORDER.indexOf(
            b as (typeof POOL_TYPE_DISPLAY_ORDER)[number],
        );
        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
    });
}

/** When `excludedPoolTypes` is empty, all entries are returned. */
export function filterLedgerEntriesExcludingPoolTypes(
    entries: LedgerEntrySummary[],
    excludedPoolTypes: ReadonlySet<string>,
): LedgerEntrySummary[] {
    if (excludedPoolTypes.size === 0) return entries;
    return entries.filter((entry) => !excludedPoolTypes.has(entry.poolType));
}

/** Groups ledger rows that share direct artifact linkage, otherwise falls back to source + row idempotency key. */
export function groupLedgerByLinkedOrBatch(
    entries: LedgerEntrySummary[],
): Array<{ groupKey: string; entries: LedgerEntrySummary[] }> {
    const map = new Map<string, LedgerEntrySummary[]>();
    for (const entry of entries) {
        const key = linkedOrBatchGroupKey(entry);
        const bucket = map.get(key);
        if (bucket) bucket.push(entry);
        else map.set(key, [entry]);
    }
    return [...map.entries()]
        .map(([groupKey, grouped]) => ({
            groupKey,
            entries: [...grouped].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            ),
        }))
        .sort(
            (a, b) =>
                new Date(a.entries[0]?.createdAt ?? 0).getTime() -
                new Date(b.entries[0]?.createdAt ?? 0).getTime(),
        )
        .reverse();
}

const PULSE_EVENT_TYPE = 'pulse';
const PULSE_LINKED_ARTIFACT_TYPES = new Set([
    'pulse',
    'pulse_set_path',
    'pulse_compare',
    'pulse_goal_navigation',
    'pulse_findings_task',
    'automated_insight_session_task',
]);

function isPulseArtifactFamily(
    artifactType: string | null | undefined,
    eventType?: string | null,
): boolean {
    if (eventType?.trim() === PULSE_EVENT_TYPE) {
        return true;
    }
    const trimmed = artifactType?.trim();
    return Boolean(
        trimmed && (PULSE_LINKED_ARTIFACT_TYPES.has(trimmed) || trimmed.startsWith('pulse_')),
    );
}

function linkedOrBatchGroupKey(entry: LedgerEntrySummary): string {
    const linkedArtifactType = entry.linkedArtifactType?.trim();
    const linkedArtifactId = entry.linkedArtifactId?.trim();
    if (linkedArtifactId && isPulseArtifactFamily(linkedArtifactType, entry.eventType)) {
        return `la:pulse:${linkedArtifactId}`;
    }
    if (linkedArtifactType && linkedArtifactId) {
        return `la:${linkedArtifactType}:${linkedArtifactId}`;
    }
    const sys = entry.sourceSystem?.trim() || 'unknown-system';
    const ev = entry.idempotencyKey?.trim() || `singleton:${entry.id}`;
    return `ev:${sys}:${ev}`;
}

export interface LedgerThreadRow {
    root: LedgerEntrySummary;
    children: LedgerEntrySummary[];
}

/** Rolls refunds/reassignments and row-linked admin adjustments under their originating row. */
export function buildLedgerThreads(entries: LedgerEntrySummary[]): LedgerThreadRow[] {
    const byId = new Map(entries.map((e) => [e.id, e]));
    const childIds = new Set<string>();

    const childrenByParent = new Map<string, LedgerEntrySummary[]>();

    const parentKeyFor = (entry: LedgerEntrySummary): string | null => {
        if (entry.linkedLedgerEntryId && byId.has(entry.linkedLedgerEntryId)) {
            return entry.linkedLedgerEntryId;
        }
        return null;
    };

    for (const entry of entries) {
        const parentId = parentKeyFor(entry);
        if (!parentId) continue;
        const list = childrenByParent.get(parentId);
        if (list) list.push(entry);
        else childrenByParent.set(parentId, [entry]);
        childIds.add(entry.id);
    }

    for (const [, kids] of childrenByParent) {
        kids.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    const roots = entries.filter((e) => !childIds.has(e.id));
    roots.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return roots.map((root) => ({
        root,
        children: childrenByParent.get(root.id) ?? [],
    }));
}

export function sumLedgerAmounts(rows: LedgerEntrySummary[]): number {
    return rows.reduce((sum, row) => sum + Number(row.amount), 0);
}

export type LedgerReservationState = 'pending' | 'committed' | 'cancelled';

const PENDING_HOLD_ENTRY_TYPES = new Set(['usage_hold', 'hold_adjustment', 'hold_release']);

function ledgerEntryType(entry: LedgerEntrySummary): string {
    return entry.type ?? entry.entryType ?? '';
}

/** Infer reservation lifecycle from its ledger rows (mirrors backend credit-events inference). */
export function inferReservationStateFromLedgerEntries(
    entries: LedgerEntrySummary[],
): LedgerReservationState {
    if (entries.length === 0) {
        return 'pending';
    }
    if (entries.some((row) => ledgerEntryType(row) === 'usage_commit')) {
        return 'committed';
    }
    const pendingAmount = entries.reduce((total, row) => {
        const type = ledgerEntryType(row);
        if (!PENDING_HOLD_ENTRY_TYPES.has(type)) {
            return total;
        }
        return total + Number(row.amount);
    }, 0);
    if (Math.abs(pendingAmount) < 0.00005) {
        return 'cancelled';
    }
    return 'pending';
}

/** Map reservation id → inferred lifecycle state from all ledger rows in the batch. */
export function buildReservationStateMap(
    allEntries: LedgerEntrySummary[],
): Map<string, LedgerReservationState> {
    const byReservation = new Map<string, LedgerEntrySummary[]>();
    for (const entry of allEntries) {
        const id = entry.creditReservationId?.trim();
        if (!id) continue;
        const list = byReservation.get(id);
        if (list) list.push(entry);
        else byReservation.set(id, [entry]);
    }
    const map = new Map<string, LedgerReservationState>();
    for (const [id, entries] of byReservation) {
        map.set(id, inferReservationStateFromLedgerEntries(entries));
    }
    return map;
}

/**
 * Override per-row `state` for reservation-tied ledger rows so holds/releases follow the
 * reservation lifecycle (committed/cancelled), not the hold entry type alone.
 */
export function applyReservationAwareLedgerStates(
    allEntries: LedgerEntrySummary[],
): LedgerEntrySummary[] {
    const stateMap = buildReservationStateMap(allEntries);
    return allEntries.map((entry) => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) return entry;
        const reservationState = stateMap.get(reservationId);
        if (!reservationState) return entry;
        return { ...entry, state: reservationState };
    });
}

/** Pending vs committed section placement (cancelled reservations land in Committed). */
export function ledgerEntrySectionKey(entry: LedgerEntrySummary): 'pending' | 'committed' {
    return entry.state === 'pending' ? 'pending' : 'committed';
}

/** Aggregate state for a composite row: pending if any row is pending, otherwise committed. */
export function compositeLedgerState(entries: LedgerEntrySummary[]): string {
    return entries.some((e) => ledgerEntrySectionKey(e) === 'pending') ? 'pending' : 'committed';
}

/** Latest `createdAt` in a group (ms since epoch). */
export function groupLatestTimestamp(entries: LedgerEntrySummary[]): number {
    return Math.max(...entries.map((e) => new Date(e.createdAt).getTime()));
}

/** Newest-first order, matching `listLedgerEntries` / raw tab ordering. */
export function ledgerEntriesNewestFirst(entries: LedgerEntrySummary[]): LedgerEntrySummary[] {
    return [...entries].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}

/** Chronologically first row (oldest); used as the “original” description source for linked batches. */
export function chronologicallyFirstLedgerEntry(
    entries: LedgerEntrySummary[],
): LedgerEntrySummary | undefined {
    if (entries.length === 0) return undefined;
    return [...entries].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )[0];
}

export function threadMembersInDisplayOrder(
    root: LedgerEntrySummary,
    children: LedgerEntrySummary[],
): LedgerEntrySummary[] {
    return ledgerEntriesNewestFirst([root, ...children]);
}
